import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';

const FooterContainer = styled.footer`
  background: var(--primary-blue-dark, #0f172a);
  color: #f8fafc;
  padding: 5rem 2rem 2rem;
  font-family: 'Plus Jakarta Sans', sans-serif;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(251, 191, 36, 0.3), transparent);
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 3.5rem;
  max-width: 1300px;
  margin: 0 auto 4rem;
`;

const BrandColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FooterLogo = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  h2 {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    color: white;
    margin: 0;
  }

  span {
    display: block;
    font-size: 0.75rem;
    color: var(--accent-gold);
    font-family: 'Plus Jakarta Sans', sans-serif;
    letter-spacing: 0.5px;
  }
`;

const LogoBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: var(--primary-blue-light);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1rem;
`;

const BrandDesc = styled.p`
  color: #94a3b8;
  font-size: 0.925rem;
  line-height: 1.7;
  margin: 0;
`;

const SectionTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.25rem;
  color: white;
  margin: 0 0 1.5rem 0;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 32px;
    height: 3px;
    background: var(--accent-gold-gradient);
    border-radius: var(--radius-full);
    margin-top: 0.6rem;
  }
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const FooterLink = styled(Link)`
  color: #94a3b8;
  text-decoration: none;
  font-size: 0.925rem;
  font-weight: 500;
  transition: var(--transition-fast);
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;

  &:hover {
    color: var(--accent-gold-light);
    transform: translateX(4px);
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 1.1rem;
  color: #94a3b8;
  font-size: 0.925rem;
  line-height: 1.6;

  svg {
    flex-shrink: 0;
    color: var(--accent-gold-light);
    margin-top: 0.2rem;
  }
`;

const MapFrame = styled.div`
  border-radius: var(--radius-md);
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 0.85rem;
  margin-top: 0.5rem;
`;

const SocialIcon = styled.a`
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.06);
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-smooth);
  border: 1px solid rgba(255, 255, 255, 0.08);

  &:hover {
    color: white;
    background: var(--primary-blue-light);
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(37, 99, 235, 0.35);
  }
`;

const BottomBar = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  color: #64748b;
  font-size: 0.875rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterGrid>
        <BrandColumn>
          <FooterLogo to="/">
            <LogoBadge>SG</LogoBadge>
            <div>
              <h2>Shanmuga Group</h2>
              <span>INSTITUTIONS • SALEM</span>
            </div>
          </FooterLogo>
          <BrandDesc>
            Empowering healthcare pioneers with world-class medical education, state-of-the-art practical laboratories, and compassionate community care.
          </BrandDesc>
          <SocialLinks>
            <SocialIcon href="#" aria-label="Facebook"><Facebook size={18} /></SocialIcon>
            <SocialIcon href="#" aria-label="Twitter"><Twitter size={18} /></SocialIcon>
            <SocialIcon href="#" aria-label="Instagram"><Instagram size={18} /></SocialIcon>
            <SocialIcon href="#" aria-label="LinkedIn"><Linkedin size={18} /></SocialIcon>
          </SocialLinks>
        </BrandColumn>

        <div>
          <SectionTitle>Quick Navigation</SectionTitle>
          <LinkList>
            <li><FooterLink to="/Group/About">About Shanmuga Group <ArrowUpRight size={14} /></FooterLink></li>
            <li><FooterLink to="/ShanmugaNursingCollege/About">Shanmuga College of Nursing <ArrowUpRight size={14} /></FooterLink></li>
            <li><FooterLink to="/ShanmugaNursingCollege/Courses">Academic Programmes <ArrowUpRight size={14} /></FooterLink></li>
            <li><FooterLink to="/ShanmugaNursingCollege/PlacementDetails">Placements & Career <ArrowUpRight size={14} /></FooterLink></li>
            <li><FooterLink to="/Smrft/About">SMRFT Foundation Trust <ArrowUpRight size={14} /></FooterLink></li>
            <li><FooterLink to="/ShanmugaNursingCollege/Alumni">Alumni Association <ArrowUpRight size={14} /></FooterLink></li>
          </LinkList>
        </div>

        <div>
          <SectionTitle>Campus & Contact</SectionTitle>
          <ContactItem>
            <MapPin size={18} />
            <span>
              24, Sarada College Road,<br />
              Salem - 636 007, Tamil Nadu, India.
            </span>
          </ContactItem>
          <ContactItem>
            <Phone size={18} />
            <span>0427 231 5610 / +91 94432 00000</span>
          </ContactItem>
          <ContactItem>
            <Mail size={18} />
            <span>info@shanmugacollege.com</span>
          </ContactItem>
        </div>

        <div>
          <SectionTitle>Campus Location</SectionTitle>
          <MapFrame>
            <iframe
              src="https://maps.google.com/maps?q=Shanmuga%20College%20of%20Nursing%2C%20Salem&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="180"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="College Location"
            ></iframe>
          </MapFrame>
        </div>
      </FooterGrid>

      <BottomBar>
        <div>
          &copy; {new Date().getFullYear()} Shanmuga Group of Institutions. All Rights Reserved.
        </div>
        <div style={{ display: 'flex', gap: '1.5rem' }}>
          <span>Privacy Policy</span>
          <span>Terms & Conditions</span>
          <span>Sitemap</span>
        </div>
      </BottomBar>
    </FooterContainer>
  );
};

export default Footer;
